import { Text } from '@sitecore-jss/sitecore-jss-react';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { errorMessages } from '../../../utils/errors/constants';
import useErrorState from '../../../utils/errors/useErrorState';
import { setAddressResults } from '../../../redux/slices/addressResultsSlice';
import { setIsValidPostcode } from '../../../redux/slices/appStateSlice';
import lookupPostCode from '../../../utils/prospectData/lookupPostCode';
import { resolve } from '../../../utils/object/pathResolver';
import { pushServiceDownError } from '../../../utils/analytics/datalayer';
import config from '../../../temp/config';

const PostcodeQuestion = ({
  rendering,
  fields,
  displayErrors,
  defaultValue,
  validPostcode,
  addresses,
}) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(defaultValue);
  const [error, setError] = useErrorState(rendering.uid, fields.name?.value, fields);
  const abortControllerRef = useRef(null);
  let postCodeErrorClass;
  if (validPostcode) {
    postCodeErrorClass = ' vds-form__input--success';
  } else if (error && displayErrors) {
    postCodeErrorClass = ' vds-form__input--invalid';
  } else {
    postCodeErrorClass = '';
  }

  const lookUpPostcode = useLookupPostcode(
    addresses,
    dispatch,
    abortControllerRef,
    setError,
    fields
  );

  const validateRegEx = value => {
    const val = fields.validation?.fields;

    if (fields?.excludedPrefixRegex?.value) {
      const excludedRegex = new RegExp(fields?.excludedPrefixRegex?.value, 'gi');
      if (excludedRegex.test(value.replace(/\s+/g, ''))) {
        setError(errorMessages.EXCLUDED);
        return false;
      }
    }

    if (val.regex?.value) {
      const regex = new RegExp(val.regex.value, 'gi');
      if (!regex.test(value)) {
        setError(errorMessages.INVALID);
        return false;
      }
    }
    return true;
  };

  const validatePostcode = value => {
    const validationRequired = fields.validation?.fields;
    if (!validationRequired) {
      setError(null);
      return true;
    }
    if (validationRequired.required && (!value || value === '')) {
      setError(errorMessages.EMPTY);
      return false;
    }
    if (validationRequired.minLength?.value && value.length < validationRequired.minLength.value) {
      setError(errorMessages.TOO_SHORT);
      return false;
    }
    if (validationRequired.maxLength?.value && value.length > validationRequired.maxLength.value) {
      setError(errorMessages.TOO_LONG);
      return false;
    }
    if (validateRegEx(value) === false) {
      return false;
    }
    setError(null);
    return true;
  };

  const handlePostcodeUpdate = e => {
    const { value } = e.target;
    let sanitizedValue = value.trim();
    if (validatePostcode(sanitizedValue)) {
      setInputValue(sanitizedValue);
      if (fields.validation?.fields?.serviceVal?.value) {
        lookUpPostcode(sanitizedValue);
      } else {
        dispatch({
          type: fields.reduxDispatchType?.value,
          payload: sanitizedValue,
        });
      }
    } else {
      setInputValue(value);
      dispatch(setIsValidPostcode(false));
    }
  };

  const handlePostcodeBlur = element => {
    if (!(error && displayErrors)) {
      if (validatePostcode(element.target.value)) {
        if (fields.validation?.fields?.serviceVal?.value) {
          lookUpPostcode(element.target.value);
        }
      }
    }
  };

  useEffect(() => {
    if (fields.required?.value && !inputValue) {
      setError(errorMessages.EMPTY);
    }
    if (inputValue) {
      lookUpPostcode(inputValue);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="vds-form__group vds-form__group--limited-width question-postcode">
      <Text
        tag="label"
        htmlFor={fields.name?.value}
        className="vds-form__label show"
        field={fields.label}
      />
      <input
        id={fields.name?.value}
        name={fields.name?.value}
        type={fields.type?.value}
        autoFocus={true}
        placeholder={fields.placeholder?.value}
        className={`vds-form__input--uppercase text-field__input-text v-form__input-field--small-width$ vds-form__input${postCodeErrorClass}`}
        data-testid={fields.testid?.value}
        value={inputValue}
        onChange={handlePostcodeUpdate}
        onBlur={handlePostcodeBlur}
        required={fields.required?.value}
      />
      {validPostcode && !error && (
        <div className="vds-form__group">
          <div className="vds-form__validation-message vds-form__validation-message--success">
            <span className="vds-form__validation-text">{fields.success?.value}</span>
          </div>
        </div>
      )}
      {displayErrors && fields.inlineError?.value && error && (
        <div className="vds-form__group">
          <div className="vds-form__validation-message vds-form__validation-message--invalid">
            <span className="vds-form__validation-text">{error.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};
PostcodeQuestion.propTypes = {
  rendering: PropTypes.shape({
    uid: PropTypes.string,
  }).isRequired,
  fields: PropTypes.shape({
    name: PropTypes.shape({ value: PropTypes.string }),
    label: PropTypes.shape({ value: PropTypes.string }),
    type: PropTypes.shape({ value: PropTypes.string }),
    placeholder: PropTypes.shape({ value: PropTypes.string }),
    testid: PropTypes.shape({ value: PropTypes.string }),
    required: PropTypes.shape({ value: PropTypes.bool }),
    success: PropTypes.shape({ value: PropTypes.string }),
    inlineError: PropTypes.shape({ value: PropTypes.bool }),
    reduxDispatchType: PropTypes.shape({ value: PropTypes.string }),
    reduxValuePath: PropTypes.shape({ value: PropTypes.string }),
    excludedPrefixRegex: PropTypes.shape({ value: PropTypes.string }),
    validation: PropTypes.shape({
      fields: PropTypes.shape({
        required: PropTypes.bool,
        minLength: PropTypes.shape({ value: PropTypes.number }),
        maxLength: PropTypes.shape({ value: PropTypes.number }),
        regex: PropTypes.shape({ value: PropTypes.string }),
        serviceVal: PropTypes.shape({ value: PropTypes.bool }),
      }),
    }),
    empty: PropTypes.shape({ value: PropTypes.string }),
    tooShort: PropTypes.shape({ value: PropTypes.string }),
    tooLong: PropTypes.shape({ value: PropTypes.string }),
    invalid: PropTypes.shape({ value: PropTypes.string }),
  }).isRequired,
  displayErrors: PropTypes.bool,
  defaultValue: PropTypes.string,
  validPostcode: PropTypes.bool,
  addresses: PropTypes.shape({
    entityAddressList: PropTypes.arrayOf(
      PropTypes.shape({
        postCode: PropTypes.string,
      })
    ),
  }),
};

const useLookupPostcode = (addresses, dispatch, abortControllerRef, setError, fields) => {
  return postCode => {
    const currentPostCode = addresses?.entityAddressList?.[0]?.postCode;
    const normalize = val => val.replace(/\s+/g, '').toUpperCase();
    if (currentPostCode && normalize(currentPostCode) === normalize(postCode)) {
      dispatch({
        type: fields.reduxDispatchType?.value,
        payload: currentPostCode,
      });
      dispatch(setIsValidPostcode(true));
      setError(null);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setError(errorMessages.IN_FLIGHT);
    lookupPostCode(postCode, controller.signal)
      .then(response => {
        if (!response.ok) {
          pushServiceDownError(config?.endpoints?.postcodeValidation, response);
          dispatch(setIsValidPostcode(false));
          setError(errorMessages.SERVICE_INVALID);
          throw new Error(errorMessages.SERVICE_INVALID);
        }
        return response.json();
      })
      .then(data => {
        let validationManagerPostcode = data.entityAddressList?.[0]?.postCode?.trim();
        if (data?.entityAddressList && validationManagerPostcode?.includes(' ')) {
          dispatch(setAddressResults(data));
          dispatch({
            type: fields.reduxDispatchType?.value,
            payload: validationManagerPostcode,
          });
          dispatch(setIsValidPostcode(true));
          setError(null);
        } else {
          throw new Error(
            `Invalid response body from postcode validation manager:\n${JSON.stringify(data)}`
          );
        }
      })
      .catch(err => {
        if (!controller.signal.aborted && err.message !== errorMessages.SERVICE_INVALID) {
          pushServiceDownError(config?.endpoints?.postcodeValidation, null, err);
          setError(errorMessages.TECH_ERROR);
          dispatch(setIsValidPostcode(false));
        }
      });
  };
};

const mapStateToProps = (state, props) => {
  const reduxValuePath = props.fields.reduxValuePath?.value;
  let defaultValue;
  if (reduxValuePath) {
    defaultValue = resolve(reduxValuePath, state);
  }
  if (!defaultValue) {
    defaultValue = '';
  }
  return {
    ...props,
    defaultValue,
    displayErrors: state.appState.displayErrors,
    validPostcode: state.appState.validPostcode,
    addresses: state.addressResults?.addressResults,
  };
};
export default connect(mapStateToProps)(PostcodeQuestion);
